import React, { useCallback, useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import { Popover } from '../KitPopover';
import colors from '../../common/theme/colors';
import constants from '../../common/theme/constants';
import { NavLink } from './KitNavBar';
import KitNavLink from '../KitNavLink';
import KitDropDownMenu from '../Dropdowns/KitDropDownMenu';
import KitButton from '../KitButton';
import KitIcon from '../KitIcon';
import { EventName, TranslateFunc } from '../../common/types';
import { onEscape, onFocusOut as onFocusOutAction } from '../../common/events';

const MoreHorizIconContainer = styled.div`
  && {
    display: flex;
    align-items: center;
    padding: 0 1rem;
    cursor: pointer;
  }
`;

const NavItem = styled.div`
  && {
    transition: opacity 1s;
    white-space: nowrap;
  }
`;

const OrientationNav = styled.nav<{ isEllipsisMenuOpen?: boolean }>`
  && {
    display: flex;
    padding-left: 0;
    margin-bottom: 0;
    list-style: none;
    flex-direction: row;
    flex-wrap: unset;
    overflow: ${(props) => (props.isEllipsisMenuOpen ? 'visible' : 'hidden')};
    flex: 1;
  }
`;

const StyledLi = styled.li`
  list-style-type: none;
  &:hover {
    background-color: ${colors.blue_20};
  }
`;

export interface KitNavItemsProps {
  navLinks?: NavLink[];
  activeNavLinkIndex?: number;
  useReactRouter?: boolean;
  isMenuOpen: (open: boolean) => void;
  t: TranslateFunc;
}

function HorizontalNavBarItems(props: KitNavItemsProps) {
  const { useReactRouter = true } = props;
  const [linkBreakPoints, setLinkBreakPoints] = useState<Array<number>>(null); // break points used for showing or hiding ellipsis
  const refNavItems = useRef([]); // referece to all the navigation dom elements.
  const navLinksRef = useRef(props.navLinks); // have to use this since state is lost in a window scoped function
  const firstItemRef = useRef(null);
  const ellipsisPopoverRef = useRef(null);
  const ellipsisRef = useRef(null);
  const lastFocus = useRef(null);
  const refLinkBreakPoints = useRef<Array<number>>(linkBreakPoints); // have to use this since state is lost in a window scoped function
  const [ellipsisMenuNavLinks, setEllipsisNavLinks] = useState<Array<NavLink>>([]); // navigation when the screen doesn't have enough real estate to support top level.
  const refOrientationNav = useRef<HTMLDivElement>(null); // reference to the container that holds all the navigation links.
  const [isEllipsisMenuOpen, setIsEllipsisMenuOpen] = useState(false);
  const refIsEllipsisMenuOpen = useRef(isEllipsisMenuOpen);
  const refEllipsisSlidingMenu = useRef<HTMLDivElement>(null);

  const onKeyPressListener = (e) => onEscape(e, setIsEllipsisMenuOpen, ellipsisPopoverRef.current);
  const onFocusOut = (event) => onFocusOutAction(event, ellipsisRef, firstItemRef, lastFocus);

  useEffect(() => {
    refLinkBreakPoints.current = linkBreakPoints;
  }, [linkBreakPoints]);

  /**
   * function manages setting which links should go in the right menu and whether we show the ellipsis
   */
  const navResizeHandler = useCallback(() => {
    let localBreakPoints: Array<number> = refLinkBreakPoints.current;
    const windowWidth =
      window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;

    if (windowWidth <= constants.breakpoints.md) {
      // do nothing
    } else {
      if (localBreakPoints === null) {
        localBreakPoints = findAllNavigationBreakPoints();
        setLinkBreakPoints(localBreakPoints);
      }

      // find out which break point we are in;
      const navElement = refOrientationNav.current;

      if (navElement) {
        const indexOfBrokenBreakpoint = getIndexOfBrokenBreakpoint(
          navElement.offsetWidth,
          localBreakPoints
        );
        const hiddenNavLinks = [];
        if (indexOfBrokenBreakpoint !== null) {
          // eslint-disable-next-line no-plusplus
          for (let x = indexOfBrokenBreakpoint; x < navLinksRef.current.length; x++) {
            hiddenNavLinks.push(navLinksRef.current[x]);
          }
          setEllipsisNavLinks(hiddenNavLinks);
        } else {
          setEllipsisNavLinks([]);
        }
      }
    }
  }, []);

  useEffect(() => {
    const removeEllipsisMenuListeners = () => {
      document.removeEventListener(EventName.KEYUP, onKeyPressListener);
      document.removeEventListener(EventName.FOCUSOUT, onFocusOut, true);
      lastFocus.current = null; // forget last focus when menu is closed
    };

    refIsEllipsisMenuOpen.current = isEllipsisMenuOpen;
    if (isEllipsisMenuOpen) {
      firstItemRef.current?.focus();
      document.addEventListener(EventName.KEYUP, onKeyPressListener);
      document.addEventListener(EventName.FOCUSOUT, onFocusOut, true);
    } else {
      removeEllipsisMenuListeners();
    }
    return () => {
      removeEllipsisMenuListeners();
    };
  }, [isEllipsisMenuOpen]);

  useEffect(() => {
    if (props.navLinks?.length > 1) {
      setTimeout(navResizeHandler, 1000);
      navLinksRef.current = props.navLinks;
    }
  }, [props.navLinks, navResizeHandler]);

  /**
   * this useEffect takes care of all the window/document event listeners
   */
  useEffect(() => {
    const onMouseDownListener = (e) => {
      if (
        refEllipsisSlidingMenu.current &&
        refIsEllipsisMenuOpen &&
        !refEllipsisSlidingMenu.current.contains(e.target)
      ) {
        setIsEllipsisMenuOpen(false);
      }
    };
    document.addEventListener(EventName.MOUSEDOWN, onMouseDownListener);
    window.addEventListener(EventName.RESIZE, navResizeHandler);
    return () => {
      window.removeEventListener(EventName.RESIZE, navResizeHandler);
      document.removeEventListener(EventName.MOUSEDOWN, onMouseDownListener);
    };
  }, [navResizeHandler]);

  /**
   * function finds all the breakpoints for the nav items
   */
  function findAllNavigationBreakPoints(): Array<number> {
    const ELLIPSIS_PAD = 40;
    const navWidths: Array<number> = [];
    const breakpoints: Array<number> = [];

    refNavItems.current.forEach((d) => {
      if (d) {
        navWidths.push(d.offsetWidth);
      }
    });
    if (navWidths.length) {
      const total = navWidths.reduce((lastValue, currentValue) => {
        breakpoints.push(lastValue + ELLIPSIS_PAD);
        return lastValue + currentValue;
      });
      breakpoints.push(total);
    }
    return breakpoints;
  }

  /**
   * function returns the index of the last nav item that is over the break point
   * @param containerWidth
   * @param breakpoints
   */
  function getIndexOfBrokenBreakpoint(containerWidth: number, breakpoints: Array<number>) {
    let brokenBreakpointIndex = null;

    if (containerWidth >= breakpoints[breakpoints.length - 1]) {
      // do nothing;
      return brokenBreakpointIndex;
    }

    // eslint-disable-next-line no-plusplus
    for (let x = breakpoints.length - 1; x >= 0; x--) {
      if (containerWidth < breakpoints[x]) {
        brokenBreakpointIndex = x;
      }
    }

    return brokenBreakpointIndex;
  }

  const renderEllipsisMenu = () => {
    const dropdownItems = [];
    const totalLinks = props.navLinks.length;
    const totalHiddenLinks = ellipsisMenuNavLinks.length;
    const indexPad = totalLinks - totalHiddenLinks;
    const hiddenActiveIndex = props.activeNavLinkIndex - indexPad;
    ellipsisMenuNavLinks.forEach((link, i) => {
      const isActive = i === hiddenActiveIndex;
      dropdownItems.push(
        <StyledLi key={link.name}>
          <KitNavLink
            ref={i === 0 ? firstItemRef : null}
            useReactRouter={useReactRouter}
            isActive={isActive}
            name={link.name}
            path={link.path}
            target={link.target}
            onClick={(e) => {
              if (link.onSelect) {
                link.onSelect(e);
              }
              props.isMenuOpen(false);
              setIsEllipsisMenuOpen(false);
            }}
            t={props.t}
          />
        </StyledLi>
      );
    });
    return <KitDropDownMenu ref={ellipsisRef}>{dropdownItems}</KitDropDownMenu>;
  };

  const renderNavLinks = () => {
    const navLinks = [];
    if (props.navLinks?.length) {
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < props.navLinks.length; i++) {
        const navLink = props.navLinks[i];
        let firstHidden = null;
        let isHidden = null;
        isHidden = ellipsisMenuNavLinks.find((link) => {
          firstHidden = firstHidden === null;
          return navLink.name === link.name;
        });

        const isActive = i === props.activeNavLinkIndex;
        navLinks.push(
          <React.Fragment key={navLink.name}>
            <NavItem
              ref={
                // eslint-disable-next-line no-return-assign
                (el) => (refNavItems.current[i] = el)
              }
              style={{ opacity: isHidden ? '0' : '1', display: isHidden ? 'none' : 'block' }}
            >
              <KitNavLink
                useReactRouter={useReactRouter}
                isActive={isActive}
                name={navLink.name}
                path={navLink.path}
                target={navLink.target}
                onClick={navLink.onSelect}
                t={props.t}
              />
            </NavItem>

            {isHidden && firstHidden && (
              <MoreHorizIconContainer>
                <Popover
                  reposition
                  isOpen={isEllipsisMenuOpen}
                  positions={['bottom', 'right', 'left', 'top']}
                  padding={10}
                  ref={ellipsisPopoverRef}
                  onClickOutside={() => {
                    props.isMenuOpen(false);
                    setIsEllipsisMenuOpen(false);
                  }}
                  content={renderEllipsisMenu()}
                  align='start'
                  containerStyle={{ zIndex: '1' }}
                >
                  <KitButton
                    variant='circle'
                    onClick={() => {
                      props.isMenuOpen(!isEllipsisMenuOpen);
                      setIsEllipsisMenuOpen(!isEllipsisMenuOpen);
                    }}
                  >
                    <KitIcon
                      icon='ellipsis-horizontal'
                      size={`${constants.iconSize.m}rem`}
                      fill={colors.gray_70}
                    />
                  </KitButton>
                </Popover>
              </MoreHorizIconContainer>
            )}
          </React.Fragment>
        );
      }
    }
    return (
      <OrientationNav
        aria-label={props.t('kit:nav_bar.aria.navigation_main')}
        isEllipsisMenuOpen={isEllipsisMenuOpen}
        ref={refOrientationNav}
      >
        {navLinks}
      </OrientationNav>
    );
  };

  return renderNavLinks();
}

export default HorizontalNavBarItems;
